import React, { useEffect, useState } from "react";
import "./SingleFilter.scss";
import { useIntl } from "react-intl";
import CustomInput, {
  CustomInputLabel,
} from "src/app/components/CustomInput/CustomInput";
import { translateMessage } from "src/app/methods/translateMessage";
import { DictionaryElementFilterSelect } from "src/app/components/DictionaryFilters/components/DictionaryElementFilterSelect/DictionaryElementFilterSelect";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import { SingleSelectFilterOptions } from "../SingleSelectFilterOptions/SingleSelectFilterOptions";
import { SingleDatePickerRange } from "../SingleDatePickerRange/SingleDatePickerRange";
import { MemberSelect } from "../MemberSelect/MemberSelect";
import { SingleCheckboxFilter } from "../SingleCheckboxFilter/SingleCheckboxFilter";
import { SingleRangeBox } from "../SingleRangeBox/SingleRangeBox";
import { SinglePercentFilter } from "../SinglePercentFilter/SinglePercentFilter";
import SingleRatingFilter from "../SingleRatingFilter/SingleRatingFilter";
import ProjectSelectorFilter from "../ProjectSelectorFilter/ProjectSelectorFilter";
import { SingleRelatedTaskFilter } from "../SingleRelatedTaskFilter/SingleRelatedTaskFilter";

const SUPPORTED_METAFIELD_TYPES = [
  MetaFieldType.Text,
  MetaFieldType.BoolVal,
  MetaFieldType.Percent,
  MetaFieldType.Number,
  MetaFieldType.Currency,
  MetaFieldType.Date,
  MetaFieldType.SingleSelect,
  MetaFieldType.MultiSelect,
  MetaFieldType.Member,
  MetaFieldType.DictionaryElement,
  MetaFieldType.Rating,
  MetaFieldType.Project,
  MetaFieldType.Content,
  MetaFieldType.Creator,
  MetaFieldType.PublicationRelated,
];

function SingleFilter({ col, sendFilter, defaultValue, filters, dataType }) {
  const intl = useIntl();

  const [waitEvent, setWaitEvent] = useState(null);

  const [value, setValue] = useState(defaultValue?.from || defaultValue);

  const labelHtmlFor =
    typeof col.name === "string"
      ? col.name.toLowerCase().split(" ").join("-")
      : "";

  useEffect(() => {
    let timer = 0;
    if (waitEvent && sendFilter) {
      timer = setTimeout(() => {
        sendFilter(col.uuid, waitEvent.target.value);
      }, 500);
    }
    return () => clearTimeout(timer);
  }, [waitEvent]);

  const onInputChange = (event) => {
    setValue(event.target.value);
    event.persist();
    setWaitEvent(event);
  };

  const renderInput = () => {
    switch (col.type) {
      case MetaFieldType.Text:
        return (
          <CustomInput
            value={value}
            onChange={onInputChange}
            type="text"
            placeholder={translateMessage({
              intl,
              id: "ws_enter_text",
              defaultMessage: "Enter text",
            })}
            data-qa={col.name}
            id={labelHtmlFor}
          />
        );
      case MetaFieldType.BoolVal:
        return (
          <SingleCheckboxFilter
            col={col}
            sendFilter={sendFilter}
            setWaitEvent={setWaitEvent}
            filters={filters}
          />
        );
      case MetaFieldType.Percent:
        return (
          <SinglePercentFilter
            col={col}
            setWaitEvent={setWaitEvent}
            filters={filters}
            id={labelHtmlFor}
          />
        );
      case MetaFieldType.Number:
      case MetaFieldType.Currency:
        return (
          <SingleRangeBox
            col={col}
            sendFilter={sendFilter}
            setWaitEvent={setWaitEvent}
            filters={filters}
            id={labelHtmlFor}
          />
        );
      case MetaFieldType.Date:
        return (
          <SingleDatePickerRange
            col={col}
            sendFilter={sendFilter}
            filters={filters}
          />
        );
      case MetaFieldType.MultiSelect:
      case MetaFieldType.SingleSelect:
        return (
          <SingleSelectFilterOptions
            col={col}
            sendFilter={sendFilter}
            displayNotSetOption={col.type === MetaFieldType.SingleSelect}
            filters={filters}
          />
        );
      case MetaFieldType.Member:
        return (
          <MemberSelect
            col={col}
            sendFilter={sendFilter}
            filters={filters}
            dataType={dataType}
          />
        );

      case MetaFieldType.DictionaryElement:
        return (
          <DictionaryElementFilterSelect
            col={col}
            sendFilter={sendFilter}
            filters={filters}
          />
        );

      case MetaFieldType.Rating:
        return (
          <SingleRatingFilter
            col={col}
            sendFilter={sendFilter}
            filters={filters}
          />
        );

      case MetaFieldType.Project:
        return (
          <ProjectSelectorFilter
            sendFilter={sendFilter}
            col={col}
            filters={filters}
            labelHtmlFor={labelHtmlFor}
          />
        );

      case MetaFieldType.Content:
      case MetaFieldType.Creator:
      case MetaFieldType.PublicationRelated:
        return (
          <SingleRelatedTaskFilter
            col={col}
            sendFilter={sendFilter}
            filters={filters}
          />
        );

      default:
        return <div>{col.type}</div>;
    }
  };

  return SUPPORTED_METAFIELD_TYPES.includes(col.type) ? (
    <div className="single-filter">
      <CustomInputLabel htmlFor={labelHtmlFor}>{col.name}</CustomInputLabel>
      {renderInput()}
    </div>
  ) : null;
}

export default SingleFilter;
